
import { IVeriMapChildComponent, IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IGoogleMapsMarkerOptions } from "@interfaces/IGoogleMapsMarkerOptions";
import { IJourney } from "@interfaces/IJourney";
import { IPlot, IPlotEvent } from "@interfaces/IPlot";
import { GoogleService } from "@services/google.service";
import { EventMarkerType, GoogleMarkerService } from "@services/googleMarker.service";
import { JourneyService } from "@services/journey.service";
import { MapService } from "@services/map.service";
import { PlotService } from "@services/plot.service";
import { VeriMarkerService } from "@services/veriMarker.service";
import { ClusterIcon, IMarkerEvents, IMarkerOptions, IPolyline, NgMapMarkerClusterer } from "@typings/google.maps.angular";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { IJourneyGraphExtremes } from "../../../../main/components/livemap/journeys/journeyGraph.component";

interface IVeriMapJourneyBindings {

    veriMap: IVeriMapComponent;

    highlightedPlot: IPlot;
    selectedPlot: IPlot;
    journeys: IJourney[];

    highlightedJourney: IJourney;
    selectedJourney: IJourney;

    selectedEvent: IPlotEvent;
    highlightedEvent: IPlotEvent;

    fit: boolean;

    displayPlots: IPlot[];
    showPlots: boolean;
    showEvents: boolean;
    showCurrentPositionOnly: boolean;
    showJourneyHover: boolean;
    snapRoads: boolean;

    onJourneyPlotHover(args: { $plot: IPlot });
    onJourneyExtremesChange(extremes: { $extremes: IJourneyGraphExtremes });
    onEventMarkerClicked(args: { $event: IPlotEvent, mouseEvent: MouseEvent });
    onPlotMarkerClicked(args: { $event: IPlot, mouseEvent: MouseEvent });
    onPlotMarkerRightClicked(args: { $event: IPlot, mouseEvent: MouseEvent });
}

class VeriMapJourneyComponentController implements angular.IComponentController, IVeriMapJourneyBindings, IVeriMapChildComponent {
    journeyEventMarkerEvents: IMarkerEvents<IPlotEvent> = {
        click: (markerObj, eventName: string, plotEvent: IPlotEvent, mouseEvent: MouseEvent) => {
            if (this.onEventMarkerClicked) {
                this.onEventMarkerClicked({ $event: plotEvent, mouseEvent: mouseEvent });
            }
        }
    }

    plotMarkerEvents: IMarkerEvents<IPlot> = {
        click: (markerObj, eventName: string, plot: IPlot, mouseEvent: MouseEvent) => {
            if (!(plot as IPlot).Id) {
                if (!this.highlightedPlot) {
                    return;
                }

                plot = this.highlightedPlot;
            }

            if (this.onPlotMarkerClicked) {
                this.onPlotMarkerClicked({ $event: plot, mouseEvent: mouseEvent });
                this.$scope.$apply();
            }
        },
        rightclick: (markerObj, eventName: string, plot: IPlot, mouseEvent: MouseEvent) => {
            if (this.onPlotMarkerRightClicked) {
                this.onPlotMarkerRightClicked({ $event: plot, mouseEvent: mouseEvent });
                this.$scope.$apply();
            }
        },
        mouseover: (markerObj, eventName: string, plot: IPlot, mouseEvent: MouseEvent) => {
            if (!(plot as IPlot).Id) {
                if (!this.highlightedPlot) {
                    return;
                }

                plot = this.highlightedPlot;
            }

            this.setHighlightedPlot(plot);
        }
    }

    veriMap: IVeriMapComponent;

    highlightedPlot: IPlot;
    selectedPlot: IPlot;
    journeyPlotOptions: IGoogleMapsMarkerOptions;

    journeys: IJourney[] = [];
    journeyPolylines: IPolyline[] = [];
    journeyEvents: IPlotEvent[] = [];
    videoEvents: IPlotEvent[] = [];

    highlightedJourney: IJourney;
    selectedJourney: IJourney;
    selectedEvent: IPlotEvent;
    highlightedEvent: IPlotEvent;

    extremes: IJourneyGraphExtremes;

    latLngsToFitTo: google.maps.LatLng[] = [];

    fit: boolean;

    displayPlots: IPlot[];
    internalDisplayPlots: IPlot[];
    showPlots: boolean;
    showEvents: boolean;
    showCurrentPositionOnly: boolean;
    showJourneyHover: boolean = true;
    snapRoads: boolean;

    journeyExtremes: IJourneyGraphExtremes;
    ignoreNextExtremesUpdated = false;

    onJourneyPlotHover: (args: { $plot: IPlot }) => void;
    onJourneyExtremesChange: (extremes: { $extremes: IJourneyGraphExtremes }) => void;
    onEventMarkerClicked: (args: { $event: IPlotEvent, mouseEvent: MouseEvent }) => void;
    onPlotMarkerClicked: (args: { $event: IPlot, mouseEvent: MouseEvent }) => void;
    onPlotMarkerRightClicked: (args: { $event: IPlot, mouseEvent: MouseEvent }) => void;

    eventMarkerOptions: IMarkerOptions<IPlotEvent> = {
        events: {},
        control: {},
        type: "cluster",
        typeOptions: {
            imagePath: "https://rawgit.com/googlemaps/js-marker-clusterer/gh-pages/images/m",
            maxZoom: 13,
            gridSize: 40 // default 60
        },
        typeEvents: {}
    };

    static $inject = ["$scope", "$q", "journeyService", "plotService", "mapService", "veriMarkerService", "googleMarkerService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly $q: angular.IQService,
        private readonly journeyService: JourneyService,
        private readonly plotService: PlotService,
        private readonly mapService: MapService,
        private readonly veriMarkerService: VeriMarkerService,
        private readonly googleMarkerService: GoogleMarkerService) {
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        var parseJourneys = false;
        var parseJourneyEvents = false;
        var showHideJourneyEvents = false;

        if (onChangesObj["journeys"]) {
            if (this.journeys && !(this.journeys instanceof Array)) {
                this.journeys = [this.journeys as any];
            }

            parseJourneys = true;
            parseJourneyEvents = true;

            this.highlightedPlot = null;
            if (this.showCurrentPositionOnly) {
                showHideJourneyEvents = true;
            }
        }

        if (onChangesObj["highlightedJourney"]) {
            this.highlightJourneyPath();
            showHideJourneyEvents = true;
        }

        if (onChangesObj["selectedJourney"]) {
            this.highlightJourneyPath();
            showHideJourneyEvents = true;
            this.createLatLngsForJourneys();

            this.fitMap();
        }

        if (onChangesObj["highlightedPlot"] && onChangesObj["highlightedPlot"].previousValue !== onChangesObj["highlightedPlot"].currentValue) {
            this.setHighlightedPlot(this.highlightedPlot);
        }

        if (onChangesObj["selectedPlot"] && onChangesObj["selectedPlot"].previousValue !== onChangesObj["selectedPlot"].currentValue) {
            this.setHighlightedPlot(this.selectedPlot);

            this.fitMap();
        }

        if (onChangesObj["highlightedEvent"]) {
            showHideJourneyEvents = true;
        }

        if (onChangesObj["selectedEvent"]) {
            if (this.selectedEvent) {
                this.panToSelectedEvent();
            }
            else {
                this.fitMap();
            }
        }

        if (onChangesObj["extremes"] && !onChangesObj["extremes"].isFirstChange()) {
            if (!this.ignoreNextExtremesUpdated) {
                this.createLatLngsForExtremes();
                this.fitMap();
            }
            else {
                this.ignoreNextExtremesUpdated = false;
            }
        }

        if (onChangesObj["displayPlots"]) {
            this.hidePlotsBasedOnZoomLevel();
        }

        if (parseJourneys) { this.parseJourneys(this.journeys); }
        if (parseJourneyEvents) { this.parseJourneyEvents(this.journeys); }
        if (showHideJourneyEvents) { this.showHideJourneyEvents(); }
    }

    $onInit(): void {
        this.veriMap.mapInitialisedPromise
            .then(map => {
                this.setJourneyExtremes();

                this.fitMap();
            })
            .catch(e => {
                console.error("onInit error", e);
            });

        this.veriMap.registerChildComponent(this);

        this.eventMarkerOptions.typeEvents.clusteringend = (ngMapMarkerClusterer) => { this.onClusteringEnd(ngMapMarkerClusterer); };

        if (this.showJourneyHover === undefined) {
            this.showJourneyHover = true;
        }
    }

    $onDestroy(): void {
        this.veriMap.deregisterChildComponent(this);
    }

    createLatLngsForJourneys() {
        const plotsFitToFilter = this.getPlotsForJourneys();

        this.latLngsToFitTo = this.mapService.convertPlotsToGoogleLatLng(plotsFitToFilter);
    }

    createLatLngsForExtremes() {
        this.latLngsToFitTo = [];
        const plotsFitToFilter = this.getPlotsForJourneys();

        for (let i = 0; i < plotsFitToFilter.length; i++) {
            const plot = plotsFitToFilter[i];
            const plotUtcValue = plot.UtcMoment.valueOf();

            if (!this.extremes || plotUtcValue >= this.extremes.min && plotUtcValue <= this.extremes.max) {
                this.latLngsToFitTo.push(new google.maps.LatLng(plot.Latitude, plot.Longitude));
            }
        }
    }

    getLatLngsForFitMap(): google.maps.LatLng[] {

        if (this.selectedPlot) {
            return [new google.maps.LatLng(this.selectedPlot.Latitude, this.selectedPlot.Longitude)];
        }

        return this.latLngsToFitTo;
    }

    onMapIdle() {
        this.setJourneyExtremes();
        this.hidePlotsBasedOnZoomLevel();
        this.snapJourneyBasedOnZoomLevel();
    }

    // -------------------------------------

    snapToRoadWorking = false;

    private snapJourneyBasedOnZoomLevel() {
        //console.log("snapJourneyBasedOnZoomLevel");
        //if (!this.snapRoads) {
        //    return;
        //}

        const map = this.veriMap.getMap();
        const currentZoom = map.getZoom();

        if (currentZoom < 12 || !this.journeys || this.journeys.length === 0) {
            return;
        }

        const mapBounds = this.mapService.getBoundsWithOffset(this.veriMap.getMap(), this.veriMap.mapOffset);

        var snappingPromises: angular.IPromise<IJourney>[] = [];

        const copyOfJourneys = copy(this.journeys);

        copyOfJourneys.forEach(journey => {
            if (journey.Id <= 0 || !journey.plots) {
                return false;
            }

            var totalMinutes = moment(journey.EndUtc).diff(journey.StartUtc, "minutes");

            if (journey.plots.length < totalMinutes * 3) {
                return false;
            }
            // if more than 60 plots a min then skip this journey

            var snapThisJourney = false;
            for (let i = 0; i < journey.plots.length; i++) {
                if (snapThisJourney) {
                    continue;
                }

                const plot = journey.plots[i];

                const plotLatLng = new google.maps.LatLng(plot.Latitude, plot.Longitude);

                if (mapBounds.contains(plotLatLng)) {
                    snapThisJourney = true;
                }
            }

            // Todo, causes polylines to go mental everywhere
            //console.log("snapThisJourney", snapThisJourney, journey);
            //// For now, never snap as its just not reliable enough
            ////snapThisJourney = false;

            //if (snapThisJourney && !this.snapToRoadWorking && (!journey.SnappedLinePoints || journey.SnappedLinePoints.length === 0)) {
            //    journey.plots = null;
            //    journey.driver = null;
            //    journey.events = null;

            //    snappingPromises.push(this.googleService.snapJourneyToRoad(journey));
            //}
        });

        if (snappingPromises.length > 0) {
            this.snapToRoadWorking = true;
            this.$q.all(snappingPromises).then(results => {

                this.journeys.forEach(journey => {
                    results.forEach(snappedJourneys => {
                        if (snappedJourneys && journey.Id === snappedJourneys.Id) {
                            journey.SnappedLinePoints = snappedJourneys.SnappedLinePoints;
                        }
                    });
                });

                this.parseJourneys(this.journeys, false);
                this.snapToRoadWorking = false;
            });
        }
    }

    private showHideJourneyEvents() {
        //console.log("showHideJourneyEvents");

        if (this.journeys == null) {
            return;
        }

        var journeyToHighlight = this.highlightedJourney || this.selectedJourney;

        this.journeyEvents.forEach(event => {
            let isVisible = false;

            if (this.highlightedEvent) {
                isVisible = event == this.highlightedEvent;
            }
            else if (journeyToHighlight && event && event.plot && event.plot.UtcMoment) {
                isVisible = event.plot.UtcMoment.isBetween(journeyToHighlight.StartUtc, journeyToHighlight.EndUtc, null, "[]");
            }
            else {
                isVisible = true;
            }

            if (event && event.options && event.options.visible != isVisible) {
                event.options.visible = isVisible;
                // Removed by CJE 4.6.21 - May not be needed
                //event.options = copy(event.options);
            }
        });

        if (this.showCurrentPositionOnly && this.showEvents) {
            // If we only want to see current position and no other events

            this.journeyEvents = this.journeyEvents.filter(event => {
                return event.eventType === EventMarkerType.CurrentPosition;
            })
        }
        this.journeyEvents = this.journeyEvents.slice();
    }

    private parseJourneyEvents(journeys: IJourney[]) {

        this.journeyEvents = [];

        if (journeys == null) {
            return;
        }

        journeys.forEach(journey => {
            //journey.events.forEach(e => {
            //    this.journeyEvents.push(e);
            //});
            this.journeyEvents = this.journeyEvents.concat(journey.events);
        });
    }

    private parseJourneys(journeys: IJourney[], fitmap = true) {
        this.journeyPolylines = [];

        if (journeys == null) {
            return;
        }

        this.parseJourneyEvents(journeys);

        journeys.forEach(journey => {
            if (journey.plots || journey.LinePoints) {
                const events = {
                    mouseover: (p: google.maps.Polyline, eventName: string, model: any, args: any[]) => {
                        var mouseEvent: google.maps.MouseEvent = args[0];
                        this.journeyMouseOverEvent(mouseEvent.latLng, journey);
                    }
                }

                var polyline = this.journeyService.createJourneyPolyline(journey, events);
                if (polyline) {
                    this.journeyPolylines.push(polyline);
                }
            }
            else {
                console.error("Journey contains no plots", journey, journey.plots);
            }
        });

        this.createLatLngsForJourneys();

        if (fitmap) {
            this.fitMap();
        }
    }

    private journeyMouseOverEvent(latLng: google.maps.LatLng, journey: IJourney) {
        if (this.showJourneyHover) {

            const closestPlot = this.plotService.findPlotClosestToLatLng(journey.plots, latLng);

            this.setHighlightedPlot(closestPlot);

            if (closestPlot != null) {
                if (this.onJourneyPlotHover != null) {
                    this.onJourneyPlotHover({ $plot: closestPlot });
                }
            }
        }
    }

    private setHighlightedPlot(plot: IPlot) {
        if (!plot) {
            return;
        }

        plot.veriMarkerOptions = this.veriMarkerService.createVeriMarkerOptions(plot, true);

        if (plot.veriMarkerOptions) {
            this.googleMarkerService.generateMarkerOptions(plot, plot.veriMarkerOptions).then(options => {

                plot.markerModel.options = options;

                this.highlightedPlot = plot;
            });
        }

    }

    private highlightJourneyPath() {

        var journeyToHighlight = this.highlightedJourney || this.selectedJourney;

        this.eventMarkerOptions.type = journeyToHighlight ? null : "cluster";

        this.journeyPolylines.forEach(p => {

            if (!journeyToHighlight || p.id === journeyToHighlight.Id) {
                //p.options.stroke.color = "#00426a";
                p.options.stroke.opacity = 0.9;
                p.options.stroke.weight = 4;
            }
            else {
                //p.options.stroke.color = "#00426a";
                p.options.stroke.opacity = 0.5;
                p.options.stroke.weight = 2;
            }
        });

        this.journeyPolylines = this.journeyPolylines.slice();
    }

    private hidePlotsBasedOnZoomLevel() {
        if (!this.showPlots) {
            return;
        }

        //this.displayPlots = this.plots;
        if (!this.displayPlots) {
            this.internalDisplayPlots = [];
            return;
        }

        //this.displayPlots = this.plotService.simplifyPlots(this.displayPlots, this.selectedPlot || this.highlightedPlot, 0.00001);
        this.plotService.getVisisblePlotsBasedOnZoomLevel(this.displayPlots, this.veriMap.getMap(), this.selectedPlot || this.highlightedPlot).then(displayPlots => {
            this.internalDisplayPlots = displayPlots;
        });

        //console.log(this.displayPlots.length);

        //if (this.selectedPlot && this.displayPlots) {
        //    this.displayPlots.forEach((plot, i) => {
        //        if (plot.Id === this.selectedPlot.Id) {
        //            this.displayPlots[i] = angular.copy(plot);
        //        }
        //    });
        //}
    }

    private getPlotsForJourneys(): IPlot[] {
        const plotsToFitTo: IPlot[] = [];

        if (!this.journeys) {
            return plotsToFitTo;
        }

        this.journeys.forEach(journey => {
            if (!journey.plots) {
                return;
            }

            if (!this.selectedJourney || (this.selectedJourney && journey.Id === this.selectedJourney.Id)) {
                for (let i = 0; i < journey.plots.length; i++) {
                    plotsToFitTo.push(journey.plots[i]);
                }
            }
        });

        return plotsToFitTo;
    }

    private fitMap() {
        if (!this.fit || !this.journeyPolylines || this.journeyPolylines.length === 0) {
            return;
        }

        var latLngs = this.getLatLngsForFitMap();
        this.veriMap.fitMap(latLngs);
    }

    private panToSelectedEvent() {
        if (this.selectedEvent != null) {
            this.veriMap.panMap(new google.maps.LatLng(this.selectedEvent.plot.Latitude, this.selectedEvent.plot.Longitude));
            return;
        }
    }

    private setJourneyExtremes() {
        if (this.journeys == null || this.journeys.length === 0 || this.ignoreNextExtremesUpdated) {

            this.ignoreNextExtremesUpdated = false;
            return;
        }

        let journeysMin = Number.MAX_VALUE;
        let journeysMax = Number.MIN_VALUE;

        if (this.selectedJourney) {
            journeysMin = moment(this.selectedJourney.StartUtc).valueOf();
            journeysMax = moment(this.selectedJourney.EndUtc).valueOf();
        }
        else if (this.journeys && this.journeys.length) {
            this.journeys.forEach(journey => {
                var journeyStart = moment(journey.StartUtc);
                journeysMin = Math.min(journeysMin, journeyStart.valueOf());

                var journeyEnd = moment(journey.EndUtc);
                journeysMax = Math.max(journeysMax, journeyEnd.valueOf());
            });
        }

        // If we are displaying a journey then see what portion of the journey is visible and update the graph extremes accordingly
        const extremes: IJourneyGraphExtremes = {
            min: Number.MAX_VALUE,
            max: Number.MIN_VALUE
        };

        const mapBounds = this.mapService.getBoundsWithOffset(this.veriMap.getMap(), this.veriMap.mapOffset);

        this.journeys.forEach(journey => {
            if (!journey.plots) {
                return;
            }

            for (let i = 0; i < journey.plots.length; i++) {
                const plot = journey.plots[i];

                const plotLatLng = new google.maps.LatLng(plot.Latitude, plot.Longitude);

                if (mapBounds.contains(plotLatLng) && plot.UtcMoment.isBetween(journeysMin, journeysMax, null, "[]")) {
                    extremes.min = Math.min(extremes.min, plot.UtcMoment.valueOf());
                    extremes.max = Math.max(extremes.max, plot.UtcMoment.valueOf());
                }
            }
        });

        // Only update if we have a minimum difference of N minutes
        //if (extremes.max - extremes.min > 2 * 60 * 1000) {
        //    this.journeyExtremes = extremes;
        //}

        this.journeyExtremes = extremes;
        this.ignoreNextExtremesUpdated = true;

        if (this.onJourneyExtremesChange != null) {
            //console.log("$extremes", this.journeyExtremes);
            this.onJourneyExtremesChange({ $extremes: this.journeyExtremes });
        }
    }

    private onClusteringEnd(ngMapMarkerClusterer: NgMapMarkerClusterer) {

        ngMapMarkerClusterer.getClusters().forEach(c => {
            this.updateClusterIconFunction(c.clusterIcon_);
        });
    }

    private updateClusterIconFunction(cluster: ClusterIcon) {

        if (cluster._show) {
            return;
        }

        // Extend our prototype here
        cluster._show = cluster.show;
        cluster.show = function () {

            const clusterIcon: ClusterIcon = this;

            clusterIcon._show();

            if (clusterIcon.div_ && clusterIcon.visible_) {

                this.div_.className = `event-cluster`;
            }
        }
    }
}

export class VeriMapJourneyComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;
    require: { [index: string]: string; };

    constructor() {
        this.bindings = {
            journeys: "<",
            highlightedPlot: "<",
            selectedPlot: "<",

            displayPlots: "<",
            showPlots: "<",
            showEvents: "<",
            showCurrentPositionOnly: "<",
            showJourneyHover: "<",
            snapRoads: "<",

            highlightedJourney: "<",
            selectedJourney: "<",
            selectedEvent: "<",
            highlightedEvent: "<",

            extremes: "<",

            fit: "<",

            onJourneyPlotHover: "&",
            onJourneyExtremesChange: "&",
            onEventMarkerClicked: "&",
            onPlotMarkerClicked: "&",
            onPlotMarkerRightClicked: "&"
        };

        this.require = {
            veriMap: "^veriMap"
        };

        this.controller = VeriMapJourneyComponentController;
        this.template = require("./veriMapJourneys.html");
    }
}
